// v2.22.2 (owner 2026-10-30, docs/rencana/UNGGAH-BANYAK.md): one upload queue per browser tab for file master
// files. Many files (picked, dropped or a whole folder) go in one queue and are uploaded one after the other;
// the queue lives above the pages, so changing menu never stops it. Everything outside (the upload of one file,
// hashing, cancelling on the server, the browser store) is passed in, so Jest tests it without a network.
// The server still decides everything (type by content, size, daily quota, versions per SPK, rights).
import { cekPilihan, fileSistem } from "./fileMaster";

export const S = {
  ANTRE: "ANTRE", PERIKSA: "PERIKSA", UNGGAH: "UNGGAH", SATUKAN: "SATUKAN", JEDA: "JEDA",
  SELESAI: "SELESAI", DITOLAK: "DITOLAK", DILEWATI: "DILEWATI", BATAL: "BATAL", GAGAL: "GAGAL",
  PERLU_IZIN: "PERLU_IZIN", PERLU_FILE: "PERLU_FILE", // from an earlier session: allow the stored handle / pick the file
};
const AKTIF = [S.PERIKSA, S.UNGGAH, S.SATUKAN];
const AKHIR = [S.SELESAI, S.DITOLAK, S.DILEWATI, S.BATAL];
export const MAKS_SEKALI = 100; // files accepted per addition (a folder can hold thousands)
export const UMUR_SIMPAN_MS = 47 * 3600 * 1000; // the server and the bucket drop unfinished uploads after 2 days
const JEDA_PROGRES_MS = 300;

export const LABEL = {
  ANTRE: "Menunggu giliran", PERIKSA: "Memeriksa file…", UNGGAH: "Mengunggah…", SATUKAN: "Menyatukan & memeriksa isi…",
  JEDA: "Dijeda", SELESAI: "Berhasil", DITOLAK: "Ditolak", DILEWATI: "Dilewati", BATAL: "Dibatalkan", GAGAL: "Berhenti",
  PERLU_IZIN: "Perlu izin", PERLU_FILE: "Pilih file yang sama",
};

const SISTEM = "File sistem atau tersembunyi, tidak perlu diunggah.";
const galatBawaan = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.message) || "Terjadi kesalahan";
let urut = 0;
const idBaru = () => `u${Date.now().toString(36)}${(urut++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const kunciFile = (spkId, nama, ukuran, diubah) => `${spkId}|${nama}|${ukuran}|${diubah || ""}`;

export class AntreanUnggah {
  // unggah({file, spkId, sha256, sinyal, jeda, saatProgres}) -> {file: {id, versi}}   (lib/unggahFileMaster)
  // sidik(file, {sinyal, saatProgres}) -> sha256 hex ; batalServer(fid) -> Promise
  // simpan: {taruh(rec), hapus(id)} (lib/simpanUnggah; every call may fail quietly)
  constructor({ unggah, sidik, batalServer, simpan, userId, tabId, teksGalat = galatBawaan, kini = () => Date.now(),
    jadwal = (fn, ms) => setTimeout(fn, ms) }) {
    Object.assign(this, { unggah, sidik, batalServer, teksGalat, kini, jadwal, userId, tabId });
    this.simpan = simpan || { taruh: async () => {}, hapus: async () => {} };
    this.item = [];
    this.pendengar = new Set();
    this.aktif = null; // {id, ctl, jeda}
    this.tahan = false; // set after 401: nothing new starts until the user acts
    this.henti = false;
    this.snap = null;
    this.terjadwal = false;
  }

  // ---- reading
  dengar(fn) { this.pendengar.add(fn); return () => this.pendengar.delete(fn); }
  daftar() { if (!this.snap) this.snap = this.item.map((x) => ({ ...x, file: undefined, handle: undefined, akar: undefined })); return this.snap; }
  cari(id) { return this.item.find((x) => x.id === id); }
  cariFid(fid) { return fid ? this.item.find((x) => x.fid === fid && !AKHIR.includes(x.status)) : undefined; }
  berjalan() { return this.item.some((x) => x.status === S.ANTRE || AKTIF.includes(x.status)); }
  belumSelesai() { return this.item.some((x) => [S.ANTRE, S.JEDA, S.GAGAL, ...AKTIF].includes(x.status)); }
  ringkasan() {
    const n = (st) => this.item.filter((x) => st.includes(x.status)).length;
    return { selesai: n([S.SELESAI]), ditolak: n([S.DITOLAK]), dilewati: n([S.DILEWATI]), batal: n([S.BATAL]),
      gagal: n([S.GAGAL]), jeda: n([S.JEDA]), perlu: n([S.PERLU_IZIN, S.PERLU_FILE]),
      sisa: n([S.ANTRE, ...AKTIF]), total: this.item.length };
  }

  // ---- adding. entri = [{file, handle?, akar?, jalur?}], tujuan = {spkId, spkLabel, batas, cocok?}
  tambah(entri, { spkId, spkLabel, batas, cocok = null }) {
    const ada = new Set(this.item.filter((x) => !AKHIR.includes(x.status)).map((x) => kunciFile(x.spkId, x.nama, x.ukuran, x.diubah)));
    let masuk = 0, dilewati = 0;
    for (const e of entri || []) {
      const f = e.file || e; // a File or {file, handle, akar, jalur}
      const it = { id: idBaru(), spkId, spkLabel, nama: f.name, ukuran: f.size, diubah: f.lastModified || null,
        jalur: e.jalur || "", file: f, handle: e.handle || null, akar: e.akar || null, cocok, status: S.ANTRE,
        byte: 0, total: f.size, alasan: null, fid: null, versi: null, sha256: null, dibuat: this.kini() };
      const k = kunciFile(spkId, it.nama, it.ukuran, it.diubah);
      const salah = fileSistem(f.name, e.jalur) ? SISTEM : cekPilihan(f, batas)
        || (masuk >= MAKS_SEKALI ? `Lebih dari ${MAKS_SEKALI} file sekaligus. Tambahkan sisanya setelah antrean ini.` : null)
        || (ada.has(k) ? "Sudah ada di antrean." : null);
      if (salah) { it.status = S.DILEWATI; it.alasan = salah; dilewati++; }
      else if (cocok && f.size !== cocok.ukuran_byte) {
        it.status = S.DITOLAK;
        it.alasan = `File ini berbeda dengan unggahan v${cocok.versi} yang belum selesai (ukurannya lain). Pilih file yang sama.`;
      } else { ada.add(k); masuk++; this._simpan(it); }
      this.item.push(it);
    }
    this.tahan = false;
    this._beri();
    this._jalankan();
    return { masuk, dilewati };
  }

  // ---- controls per file
  jeda(id) {
    const it = this.cari(id);
    if (!it) return;
    if (this.aktif?.id === id) {
      if (it.status === S.PERIKSA) this.aktif.ctl.abort(); // hashing: nothing to keep, stop now
      else if (it.status === S.UNGGAH) { this.aktif.jeda.diminta = true; this._ubah(it, { menjeda: true }); } // parts in flight finish first
    } else if (it.status === S.ANTRE) this._ubah(it, { status: S.JEDA });
  }

  lanjut(id) {
    const it = this.cari(id);
    if (!it || ![S.JEDA, S.GAGAL, S.DITOLAK].includes(it.status)) return;
    if (!it.file) this._ubah(it, { status: it.handle || it.akar ? S.PERLU_IZIN : S.PERLU_FILE, alasan: null });
    else this._ubah(it, { status: S.ANTRE, alasan: null, menjeda: false });
    if (it.status === S.ANTRE) this._simpan(it);
    this.tahan = false;
    this._jalankan();
  }

  async batal(id) {
    const it = this.cari(id);
    if (!it || AKHIR.includes(it.status) || it.status === S.SATUKAN) return; // joining: the server finishes it anyway
    if (this.aktif?.id === id) { it.batalDiminta = true; this.aktif.ctl.abort(); return; } // _proses cancels on the server
    await this._batalkan(it);
  }

  bersihkan() { // removes the finished rows (the summary)
    this.item = this.item.filter((x) => !AKHIR.includes(x.status));
    this._beri();
  }

  hentikan() { // logout / another account: stop the running file, keep everything in the browser store
    this.henti = true;
    this.aktif?.ctl.abort();
  }

  // ---- after the browser was closed. rec = what _simpan stored
  pulihkan(recs, tabHidup = new Set()) {
    for (const r of recs || []) {
      if (r.userId !== this.userId || tabHidup.has(r.tabId) || this.cari(r.id)) continue;
      if (!(this.kini() - (r.dibuat || 0) < UMUR_SIMPAN_MS)) { this.simpan.hapus(r.id).catch?.(() => {}); continue; }
      this.item.push({ ...r, file: null, status: r.handle || r.akar ? S.PERLU_IZIN : S.PERLU_FILE, byte: 0,
        total: r.ukuran, alasan: null, tabId: this.tabId });
    }
    this._beri();
  }

  // a File for a restored row (from its handle or picked again): same name + size + date → the stored hash is kept
  pasangFile(id, file) {
    const it = this.cari(id);
    if (!it || ![S.PERLU_IZIN, S.PERLU_FILE].includes(it.status)) return false;
    const sama = file.size === it.ukuran && (!it.diubah || !file.lastModified || file.lastModified === it.diubah);
    if (!sama) it.sha256 = null;
    Object.assign(it, { file, ukuran: file.size, total: file.size, diubah: file.lastModified || it.diubah, status: S.ANTRE, alasan: null });
    this._simpan(it);
    this.tahan = false;
    this._beri();
    this._jalankan();
    return true;
  }

  // a stored handle that no longer works (permission refused, file moved or deleted): ask for the file instead
  perluFile(id, alasan) {
    const it = this.cari(id);
    if (!it || it.status !== S.PERLU_IZIN) return;
    this._ubah(it, { alasan, ...(alasan && /tidak ditemukan/.test(alasan) ? { status: S.PERLU_FILE } : {}) });
  }

  // files picked again (browsers without stored handles): matched by name and size
  cocokkanFile(files) {
    let n = 0;
    for (const f of files || []) {
      const it = this.item.find((x) => [S.PERLU_FILE, S.PERLU_IZIN].includes(x.status) && x.nama === f.name && x.ukuran === f.size);
      if (it && this.pasangFile(it.id, f)) n++;
    }
    return n;
  }

  // ---- inside
  _beri() { this.snap = null; for (const fn of [...this.pendengar]) fn(); }
  _beriNanti() {
    if (this.terjadwal) return;
    this.terjadwal = true;
    this.jadwal(() => { this.terjadwal = false; this._beri(); }, JEDA_PROGRES_MS);
  }
  _ubah(it, patch, lambat = false) { Object.assign(it, patch); if (lambat) this._beriNanti(); else this._beri(); }
  _simpan(it) {
    const rec = { id: it.id, userId: this.userId, tabId: this.tabId, spkId: it.spkId, spkLabel: it.spkLabel, nama: it.nama,
      ukuran: it.ukuran, diubah: it.diubah, jalur: it.jalur, sha256: it.sha256, fid: it.fid, versi: it.versi,
      cocok: it.cocok, dibuat: it.dibuat, handle: it.handle, akar: it.akar };
    Promise.resolve().then(() => this.simpan.taruh(rec)).catch(() => {});
  }
  _akhir(it, status, alasan, ekstra = {}) {
    Object.assign(it, ekstra, { status, alasan, menjeda: false });
    if (AKHIR.includes(status)) Promise.resolve().then(() => this.simpan.hapus(it.id)).catch(() => {});
    this._beri();
  }
  async _batalkan(it) {
    if (it.fid) { try { await this.batalServer(it.fid); } catch { /* already finished, cancelled or expired */ } }
    this._akhir(it, S.BATAL, "Dibatalkan.");
  }
  // the server refused for a reason that holds for the rest too: no request needed for those
  _sebar(it, kode, pesan) {
    const semua = kode === 429; // daily quota of this account
    const spkIni = kode === 409 && /versi file per SPK/.test(pesan);
    if (!semua && !spkIni) return;
    for (const x of this.item) if (x !== it && x.status === S.ANTRE && (semua || x.spkId === it.spkId)) this._akhir(x, S.DITOLAK, pesan);
  }

  _jalankan() {
    if (this.aktif || this.tahan || this.henti) return;
    const it = this.item.find((x) => x.status === S.ANTRE);
    if (!it) return;
    const ctl = new AbortController();
    this.aktif = { id: it.id, ctl, jeda: { diminta: false } };
    this._proses(it, this.aktif).finally(() => { this.aktif = null; this._jalankan(); });
  }

  async _proses(it, { ctl, jeda }) {
    try {
      if (!it.sha256) {
        this._ubah(it, { status: S.PERIKSA, byte: 0, total: it.ukuran });
        const sha = await this.sidik(it.file, { sinyal: ctl.signal, saatProgres: (p) => this._ubah(it, { byte: p.byte }, true) });
        if (it.cocok && sha !== it.cocok.sha256) {
          this._akhir(it, S.DITOLAK, `File ini berbeda dengan unggahan v${it.cocok.versi} yang belum selesai (isinya lain). Pilih file yang sama.`);
          return;
        }
        it.sha256 = sha;
        this._simpan(it);
      }
      if (ctl.signal.aborted) throw Object.assign(new Error("dijeda"), { dijeda: true });
      this._ubah(it, { status: S.UNGGAH, byte: 0, byteSesi: 0, msSesi: 0 });
      const r = await this.unggah({ file: it.file, spkId: it.spkId, sha256: it.sha256, sinyal: ctl.signal, jeda,
        saatProgres: (p) => {
          const patch = { byte: p.byte, total: p.total, byteSesi: p.byteSesi, msSesi: p.msSesi, lanjut: p.lanjut };
          if (p.tahap === "satukan") patch.status = S.SATUKAN;
          if (p.fid && p.fid !== it.fid) { this._ubah(it, { ...patch, fid: p.fid, versi: p.versi }); this._simpan(it); return; }
          this._ubah(it, patch, p.tahap === "unggah");
        } });
      this._akhir(it, S.SELESAI, null, { versi: r?.file?.versi ?? it.versi, fid: r?.file?.id || it.fid, byte: it.total });
    } catch (e) {
      if (it.batalDiminta) { await this._batalkan(it); return; }
      if (e?.dijeda || ctl.signal.aborted) { this._ubah(it, { status: S.JEDA, menjeda: false }); return; }
      const kode = e?.response?.status;
      const pesan = this.teksGalat(e);
      if (kode === 401) { // the login ended: keep it, start nothing else until the user logs in and presses Lanjut
        this.tahan = true;
        this._ubah(it, { status: S.JEDA, menjeda: false, alasan: "Sesi login berakhir. Login lagi, lalu tekan Lanjut." });
      } else if (e?.response) { this._akhir(it, S.DITOLAK, pesan, { kode }); this._sebar(it, kode, pesan); }
      else this._ubah(it, { status: S.GAGAL, menjeda: false, alasan: pesan }); // network: may be tried again, stays stored
    }
  }
}
